/**
 * Prepare attributes to be parsed
 */
const qs = require('qs')

export default class Builder {
  constructor (resource) {
    this.resource = resource
    this.includes = []
    this.appends = []
    this.sorts = []
    this.pageValue = null
    this.limitValue = null
    this.payload = null

    this.uri = ''

    this.fields = {}
    this.filters = []
  }

  /**
   * Query builder
   */

  include (...args) {
    this.includes = args

    return this
  }

  append (...args) {
    this.appends = args

    return this
  }

  select (...fields) {
    if (fields.length === 0) {
      throw new Error('You must specify the fields on select() method.')
    }

    // single entity .select(['age', 'firstname'])
    if (fields[0].constructor === String || Array.isArray(fields[0])) {
      this.fields[this.resource] = fields.join(',')
    }

    // related entities .select({ posts: ['title', 'content'], user: ['age', 'firstname']} )
    if (fields[0].constructor === Object) {
      Object.entries(fields[0]).forEach(([key, value]) => {
        this.fields[key] = value.join(',')
      })
    }

    return this
  }

  where (field, operator, value) {
    if (field === undefined || operator === undefined || value === undefined) {
      throw new Error('The KEY and VALUE are required on where() method.')
    }

    if (Array.isArray(value) || value instanceof Object) {
      throw new Error('The VALUE must be primitive on where() method.')
    }

    this.filters.push({ field, operator, value })

    return this
  }

  whereIn (field, array) {
    if (!Array.isArray(array)) {
      throw new Error('The second argument on whereIn() method must be an array.')
    }

    let value = array.join(',')

    this.filters.push({ field, operator: 'eq', value })

    return this
  }

  orderBy (...args) {
    this.sorts = args

    return this
  }

  page (value) {
    if (!Number.isInteger(value)) {
      throw new Error('The VALUE must be an integer on page() method.')
    }

    this.pageValue = value

    return this
  }

  limit (value) {
    if (!Number.isInteger(value)) {
      throw new Error('The VALUE must be an integer on limit() method.')
    }

    this.limitValue = value

    return this
  }

  params (payload) {
    if (payload === undefined || payload.constructor !== Object) {
      throw new Error('You must pass a payload/object as param.')
    }

    this.payload = payload

    return this
  }

  // final query string
  query () {
    this.uri = ''

    this._includes()
    this._appends()
    this._fields()
    this._filters()
    this._sorts()
    this._page()
    this._limit()
    this._payload()

    return this.uri
  }

  /**
   * Helpers
   */

  hasIncludes () {
    return this.includes.length > 0
  }

  hasAppends () {
    return this.appends.length > 0
  }

  hasFields () {
    return Object.keys(this.fields).length > 0
  }

  hasFilters () {
    return this.filters.length > 0
  }

  hasSorts () {
    return this.sorts.length > 0
  }

  hasPage () {
    return this.pageValue !== null
  }

  hasLimit () {
    return this.limitValue !== null
  }

  hasPayload () {
    return this.payload !== null
  }

  prepend () {
    return (this.uri === '') ? '?' : '&'
  }

  parameterNames () {
    return {
      include: 'include',
      filter: 'filter',
      sort: 'sort',
      fields: 'fields',
      append: 'append',
      page: 'page',
      limit: 'perPage'
    }
  }

  /**
   * Parsers
   */

  _includes () {
    if (!this.hasIncludes()) {
      return
    }

    this.uri += this.prepend() + this.parameterNames().include + '=' + this.includes
  }

  _appends () {
    if (!this.hasAppends()) {
      return
    }

    this.uri += this.prepend() + this.parameterNames().append + '=' + this.appends
  }

  _fields () {
    if (!this.hasFields()) {
      return
    }

    let fields = { [this.parameterNames().fields]: this.fields }
    this.uri += this.prepend() + qs.stringify(fields, { encode: false })
  }

  _filters () {
    if (!this.hasFilters()) {
      return
    }

    let self = this

    this.filters.forEach(function (filter, index) {
      if (filter.operator === 'like') {
        self.uri += `${self.prepend()}${self.parameterNames().filter}[${filter.field}]=${filter.value}`
        return
      }
      self.uri += `${self.prepend()}${self.parameterNames().filter}[${filter.field}][${filter.operator}]=${filter.value}`
    })
  }

  _sorts () {
    if (!this.hasSorts()) {
      return
    }

    this.uri += this.prepend() + this.parameterNames().sort + '=' + this.sorts
  }

  _page () {
    if (!this.hasPage()) {
      return
    }

    this.uri += this.prepend() + this.parameterNames().page + '=' + this.pageValue
  }

  _limit () {
    if (!this.hasLimit()) {
      return
    }

    this.uri += this.prepend() + this.parameterNames().limit + '=' + this.limitValue
  }

  _payload () {
    if (!this.hasPayload()) {
      return
    }

    this.uri += this.prepend() + qs.stringify(this.payload, { encode: false })
  }
}
